import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DollarSign, TrendingUp, TrendingDown, Minus, Users, Calendar,
  AlertCircle, Sparkles, ExternalLink, Shield, Clock, Wallet,
} from 'lucide-react';
import {
  fetchCreatorDashboardThunk,
  fetchMonetizationConfigThunk,
  onboardConnectThunk,
} from '../../redux/monetization/monetizationActionThunks';
import {
  selectCreatorDashboard,
  selectCreatorDashboardLoading,
  selectCreatorDashboardError,
  selectMonetizationConfig,
  selectMonetizationConfigLoaded,
} from '../../redux/monetization/monetizationSlice';
import { RevenueTrendChart, DistributionBars, MiniSparkline, fmtMoney, SENTIMENT_COLORS } from './creatorDashboardCharts';
import InfoTooltip from '../../components/ui/InfoTooltip';

// Pass 31 -- built directly at the user's request, overriding the standing
// "don't build this until cold-start creator research confirms demand"
// gate (08_MASTER_TODO.md #23), which the user explicitly acknowledged and
// chose to override. Kept behind the same MONETIZATION_LIVE test-mode
// banner every other monetization page already uses, so nothing here
// implies real money is moving while the kill-switch is off.

// Full, static class strings only -- Tailwind's build-time scanner can't
// see interpolated class names like `bg-${accent}-500/10`, the exact
// dynamic-class bug already found and flagged once in this codebase
// (08_MASTER_TODO.md #13, MobileBottomBar.jsx).
const ACCENT_STYLES = {
  indigo: { iconBg: 'bg-indigo-100 dark:bg-indigo-500/10', icon: 'text-indigo-500 dark:text-indigo-400' },
  violet: { iconBg: 'bg-violet-100 dark:bg-violet-500/10', icon: 'text-violet-500 dark:text-violet-400' },
  blue: { iconBg: 'bg-blue-100 dark:bg-blue-500/10', icon: 'text-blue-500 dark:text-blue-400' },
  emerald: { iconBg: 'bg-emerald-100 dark:bg-emerald-500/10', icon: 'text-emerald-500 dark:text-emerald-400' },
};

const StatCard = ({ icon: Icon, label, value, sub, accent = 'indigo' }) => {
  const style = ACCENT_STYLES[accent] || ACCENT_STYLES.indigo;
  return (
    <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${style.iconBg}`}>
          <Icon className={`w-4 h-4 ${style.icon}`} />
        </div>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
  const style = ACCENT_STYLES[accent] || ACCENT_STYLES.indigo;
  return (
    <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-5 h-5 ${style.icon} flex-shrink-0`} />
        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
      </div>
      {subtitle && <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-4">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </div>
  );
};

const TrendBadge = ({ direction }) => {
  const { t } = useTranslation();
  const map = {
    up: { Icon: TrendingUp, cls: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/10', label: t('creator.trend.up') },
    down: { Icon: TrendingDown, cls: 'text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-500/10', label: t('creator.trend.down') },
    flat: { Icon: Minus, cls: 'text-gray-500 dark:text-gray-400 bg-gray-500/10', label: t('creator.trend.flat') },
  };
  const { Icon, cls, label } = map[direction] || map.flat;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

export default function CreatorDashboardPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((s) => s.profile?.user?.id);
  const dashboard = useSelector(selectCreatorDashboard);
  const loading = useSelector(selectCreatorDashboardLoading);
  const error = useSelector(selectCreatorDashboardError);
  const config = useSelector(selectMonetizationConfig);
  const configLoaded = useSelector(selectMonetizationConfigLoaded);

  useEffect(() => {
    if (!configLoaded) dispatch(fetchMonetizationConfigThunk());
    if (userId && !dashboard) dispatch(fetchCreatorDashboardThunk(userId));
  }, [dispatch, userId, configLoaded, dashboard]);

  const hasGroups = (dashboard?.earnings_by_group?.length || 0) > 0;
  const needsPayoutSetup = hasGroups && dashboard && !dashboard.connect_status.payouts_enabled;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Wallet className="w-7 h-7 sm:w-8 sm:h-8 text-indigo-500" />
          {t('creator.pageTitle')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('creator.pageSubtitle')}
        </p>
      </div>

      {!config.monetization_live && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600/30 rounded-xl">
          <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('creator.testModeNotice')}
          </p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-xl">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {needsPayoutSetup && (
        <div className="flex items-center justify-between gap-3 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-600/30 rounded-xl">
          <div className="flex items-start gap-3">
            <Wallet className="text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-indigo-700 dark:text-indigo-300">
              {t('creator.payoutSetupPrompt')}
            </p>
          </div>
          <button
            onClick={() => dispatch(onboardConnectThunk())}
            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
          >
            {t('creator.setUpPayouts')}
          </button>
        </div>
      )}

      {loading && !dashboard && (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent" />
        </div>
      )}

      {dashboard && !hasGroups && (
        <div className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 rounded-2xl p-10 sm:p-14 text-center">
          <Sparkles className="mx-auto text-indigo-400 dark:text-indigo-500 mb-4" size={36} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('creator.noGroupsTitle')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
            {t('creator.noGroupsSubtitle')}
          </p>
          <button
            onClick={() => navigate('/groupies')}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            {t('creator.goToGroups')}
          </button>
        </div>
      )}

      {dashboard && hasGroups && (
        <>
          {/* ---- Overview ---- */}
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-5">
              <StatCard
                icon={DollarSign}
                label={t('creator.stats.totalEarnings')}
                value={fmtMoney(dashboard.overview.total_revenue_cents)}
                sub={t('creator.stats.allTimePayout')}
              />
              <StatCard
                icon={Calendar}
                label={t('creator.stats.thisMonth')}
                value={fmtMoney(dashboard.overview.current_period_revenue_cents)}
                sub={t('creator.stats.last30Days')}
                accent="violet"
              />
              <StatCard
                icon={Users}
                label={t('creator.stats.activeSubscribers')}
                value={dashboard.overview.current_subscriber_count}
                sub={t('creator.stats.acrossAllGroups')}
                accent="blue"
              />
              <StatCard
                icon={Sparkles}
                label={t('creator.stats.estNextMonth')}
                value={fmtMoney(dashboard.estimated_next_period.estimated_cents)}
                sub={
                  dashboard.estimated_next_period.basis_subscriber_count > 0
                    ? t('creator.stats.basedOnSubs', { count: dashboard.estimated_next_period.basis_subscriber_count })
                    : t('creator.stats.notEnoughData')
                }
                accent="emerald"
              />
            </div>

            <SectionCard icon={TrendingUp} title={t('creator.revenueTrend.title')} subtitle={t('creator.revenueTrend.subtitle')}>
              <RevenueTrendChart trend={dashboard.overview.subscriber_trend} />
            </SectionCard>
          </div>

          {/* ---- Your Groups ---- */}
          <SectionCard icon={Shield} title={t('creator.yourGroups.title')} subtitle={t('creator.yourGroups.subtitle')}>
            <div className="space-y-3">
              {dashboard.earnings_by_group.map((g) => {
                const contentGroup = dashboard.content_trends.groups.find((c) => c.group_id === g.group_id);
                return (
                  <div
                    key={g.group_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700/40"
                  >
                    <div className="min-w-0">
                      <button
                        onClick={() => navigate(`/groups/${g.group_id}`)}
                        className="text-gray-900 dark:text-white font-semibold hover:underline flex items-center gap-1 truncate"
                      >
                        {g.group_name}
                        <ExternalLink size={13} className="flex-shrink-0" />
                      </button>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {fmtMoney(g.price_cents)}/{g.billing_type === 'monthly' ? t('creator.yourGroups.perMonth') : g.billing_type} ·{' '}
                        {t('creator.yourGroups.activeSubscriber', { count: g.active_subscriber_count })}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                      {contentGroup && (
                        <MiniSparkline
                          values={[contentGroup.sentiment_breakdown.bullish || 0, contentGroup.total_posts, contentGroup.sentiment_breakdown.bearish || 0]}
                        />
                      )}
                      <div className="text-right">
                        <div className="font-bold text-gray-900 dark:text-white">{fmtMoney(g.total_revenue_cents)}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{fmtMoney(g.current_period_revenue_cents)} {t('creator.yourGroups.thisMonth')}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* ---- Audience + Insights (2-col on desktop) ---- */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            <SectionCard
              icon={Users}
              title={t('creator.audience.title')}
              subtitle={t('creator.audience.subtitle')}
              accent="blue"
            >
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
                    <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">
                      +{dashboard.audience.new_last_30_days}
                    </div>
                    <div className="text-[11px] text-emerald-600/80 dark:text-emerald-400/70">{t('creator.audience.new30d')}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20">
                    <div className="text-lg font-bold text-rose-700 dark:text-rose-400">
                      -{dashboard.audience.canceled_last_30_days}
                    </div>
                    <div className="text-[11px] text-rose-600/80 dark:text-rose-400/70">{t('creator.audience.canceled30d')}</div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-2">{t('creator.audience.status')}</h4>
                  <DistributionBars
                    data={dashboard.audience.status_breakdown}
                    colors={{ active: '#34d399', canceled: '#94a3b8', past_due: '#fbbf24', expired: '#f87171' }}
                  />
                </div>

                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                      {t('creator.audience.credibilityTier')}
                    </h4>
                    <InfoTooltip text={t('creator.audience.tierTooltip')} />
                  </div>
                  {dashboard.audience.tier_breakdown ? (
                    <DistributionBars data={dashboard.audience.tier_breakdown} />
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                      {t('creator.audience.tierUnavailable')}
                    </p>
                  )}
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={Sparkles} title={t('creator.insights.title')} subtitle={t('creator.insights.subtitle')} accent="violet">
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/30">
                  <TrendingUp className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      {t('creator.insights.revenueTrendLabel')} <TrendBadge direction={dashboard.insights.revenue_trend_direction} />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{t('creator.insights.comparedTo30d')}</p>
                  </div>
                </div>

                {dashboard.insights.best_posting_day ? (
                  <div className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/30">
                    <Clock className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('creator.insights.bestDayLabel', { day: dashboard.insights.best_posting_day.day })}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('creator.insights.bestDaySubtitle', { avg: dashboard.insights.best_posting_day.avg_engagement, count: dashboard.insights.best_posting_day.sample_size })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/30">
                    <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('creator.insights.noPostingDayHint')}
                    </p>
                  </div>
                )}

                {dashboard.insights.renewal_risk_count > 0 && (
                  <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-600/20">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        {t('creator.insights.renewalRisk', { count: dashboard.insights.renewal_risk_count })}
                      </div>
                      <p className="text-xs text-amber-700/80 dark:text-amber-400/70 mt-0.5">
                        {t('creator.insights.renewalRiskHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>

          {/* ---- Content Trends ---- */}
          <SectionCard
            icon={TrendingUp}
            title={t('creator.contentTrends.title')}
            subtitle={t('creator.contentTrends.subtitle')}
            accent="blue"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-3">
                  {t('creator.contentTrends.sentimentByGroup')}
                </h4>
                <div className="space-y-4">
                  {dashboard.content_trends.groups.map((g) => (
                    <div key={g.group_id}>
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5 truncate">{g.group_name}</div>
                      {g.total_posts > 0 ? (
                        <DistributionBars data={g.sentiment_breakdown} colors={SENTIMENT_COLORS} />
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('creator.contentTrends.noPosts')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-3">
                  {t('creator.contentTrends.mostDiscussedSectors')}
                </h4>
                {dashboard.content_trends.top_sectors.length > 0 ? (
                  <DistributionBars
                    data={Object.fromEntries(dashboard.content_trends.top_sectors.map((s) => [s.sector, s.count]))}
                  />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('creator.contentTrends.noSectorsHint')}
                  </p>
                )}
              </div>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
